import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '../contexts/user'
import { crossLink, readCheckoutIntent, resumeCheckout, safeRedirect } from '../lib/checkoutIntent'

export default function SignupPage() {
  const { signup } = useUser()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const redirectTo = safeRedirect(params.get('redirect'))
  const intent = readCheckoutIntent(params)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!acceptedTerms) {
      setError('Please accept the Terms and Privacy Policy to continue.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setBusy(true)
    try {
      await signup({ email, password, name: name || undefined, acceptedTerms: true, marketingConsent })
      if (intent) {
        // The whole reason this page carries a checkout param: the buyer who was
        // lost on 31 Aug pressed Unlock, was sent here, and would have landed
        // back on a page of marketing copy with the decision already made.
        await resumeCheckout(intent, redirectTo, { onFallback: navigate })
        return
      }
      navigate(redirectTo)
    } catch (err) {
      setError((err as Error).message || 'Sign up failed')
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full rounded-smooth border border-border-strong bg-white px-4 py-3 font-body text-body text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20'

  return (
    <>
      <div className="min-h-screen bg-brand-cream flex items-start justify-center px-4 py-16 md:py-24">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-h2 font-bold text-brand-navy mb-2">
            {intent ? 'Create your account to continue' : 'Create your account'}
          </h1>
          {intent ? (
            <p className="font-body text-body-sm text-ink-muted mb-4">
              Courses are tied to an account so your progress and access follow you. This takes a
              moment and then you go straight to checkout.
            </p>
          ) : null}
          <p className="font-body text-body-sm text-ink-muted mb-8">
            Already have one?{' '}
            <Link to={crossLink('/login', params, redirectTo)} className="text-brand-navy font-semibold underline">
              Sign in
            </Link>
          </p>

          <form onSubmit={submit} className="space-y-4 bg-white border border-border-subtle rounded-smooth-lg p-6 md:p-8">
            <div>
              <label className="block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5">Name <span className="normal-case text-ink-muted/70">(optional)</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className={inputCls} />
            </div>
            <div>
              <label className="block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className={inputCls} />
            </div>
            <div>
              <label className="block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" className={inputCls} />
              <p className="font-body text-caption text-ink-muted mt-1">At least 8 characters.</p>
            </div>

            <label className="flex items-start gap-3 font-body text-body-sm text-ink-soft cursor-pointer">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0" />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-brand-navy font-semibold underline">Terms &amp; Conditions</Link> and{' '}
                <Link to="/privacy" target="_blank" className="text-brand-navy font-semibold underline">Privacy Policy</Link>.
              </span>
            </label>

            <label className="flex items-start gap-3 font-body text-body-sm text-ink-soft cursor-pointer">
              <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0" />
              <span>Email me occasional product updates and new courses. <span className="text-ink-muted">(optional)</span></span>
            </label>

            {error ? <p className="font-body text-body-sm text-red-600">{error}</p> : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-brand-navy text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? 'Creating account…' : intent ? 'Create account and continue' : 'Create account'}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
